import React, { useState } from 'react';
import { GripVertical } from 'lucide-react';
import BriefCard, { type BriefCardData } from '../../../components/Briefs/BriefCard';
import BriefDetailModal, { BriefDetailType } from '../../../components/Profile/brief/BriefDetailModal';
import { useToast } from '../../../components/UI/Toast';

export interface Brief {
    id: string | number;
    title: string;
    budgetRange: string;
    deliveryTimeRange: string;
    communicationCount: number;
    trialCount: number;
    viewCount: number;
    status?: 'draft' | 'active' | 'closed' | 'archived';
    displayOrder?: number;
    createdAt?: string;
    briefType?: 'standard' | 'lightweight';
    contactInfo?: string;
    // 详情字段
    description?: string;
    deliverables?: string;
    targetDate?: string;
    tags?: string[];
    exampleWorks?: {
        id: number | string;
        type: 'video' | 'image';
        thumbnailUrl: string;
        videoUrl?: string;
        images?: string[];
        title: string;
        description?: string;
        sourceUrl?: string;
    }[];
}

// Legacy mock export to satisfy ProfileTabs until its data fetching is fully dynamic
export const MOCK_BRIEFS: Brief[] = [];

interface BriefGridsProps {
    isOwnProfile?: boolean;
    userInfo?: { id: string; name: string; avatar: string };
    initialOpenBriefId?: string;
}

const getBriefDisplayOrder = (brief: Brief, fallbackIndex: number) => {
    if (typeof brief.displayOrder === 'number' && Number.isFinite(brief.displayOrder)) {
        return brief.displayOrder;
    }
    const timestamp = brief.createdAt ? new Date(brief.createdAt).getTime() : NaN;
    return Number.isFinite(timestamp) ? -timestamp : fallbackIndex;
};

const sortBriefsForProfile = (items: Brief[]) => (
    items
        .map((item, index) => ({ item, index }))
        .sort((a, b) => {
            const aArchived = a.item.status === 'archived' ? 1 : 0;
            const bArchived = b.item.status === 'archived' ? 1 : 0;
            if (aArchived !== bArchived) return aArchived - bArchived;

            const orderDiff = getBriefDisplayOrder(a.item, a.index) - getBriefDisplayOrder(b.item, b.index);
            if (orderDiff !== 0) return orderDiff;
            return a.index - b.index;
        })
        .map(({ item }, index) => ({ ...item, displayOrder: item.displayOrder ?? index }))
);

const moveItem = <T,>(items: T[], fromIndex: number, toIndex: number) => {
    const next = [...items];
    const [moved] = next.splice(fromIndex, 1);
    next.splice(toIndex, 0, moved);
    return next;
};

const BriefGrids: React.FC<BriefGridsProps> = ({ isOwnProfile = false, userInfo, initialOpenBriefId }) => {
    const [selectedBrief, setSelectedBrief] = useState<BriefDetailType | null>(null);
    const [briefsList, setBriefsList] = useState<Brief[]>([]);
    const [loading, setLoading] = useState(true);
    const [draggingBriefId, setDraggingBriefId] = useState<string | null>(null);
    const autoOpenedBriefIdRef = React.useRef<string | null>(null);
    const lastReloadAtRef = React.useRef(0);
    const { showToast } = useToast();
    const loadBriefs = React.useCallback(async () => {
        if (!userInfo?.id) return;
        setLoading(true);
        try {
            const { briefService } = await import('../../../services/briefService');
            const res = await briefService.getBriefs({ clientUserId: userInfo?.id, limit: 50, includeArchived: isOwnProfile });
            if (res.data && res.data.briefs) {
                const mapped = res.data.briefs.map((b: any) => ({
                    ...b,
                    budgetRange: b.budgetRange || b.budget,
                    deliveryTimeRange: b.deliveryTimeRange || b.deadline,
                    displayOrder: b.displayOrder,
                    createdAt: b.createdAt,
                    trialCount: b.trialCount || 0,
                    viewCount: b.viewCount || 0
                }));
                setBriefsList(sortBriefsForProfile(mapped));
            }
        } catch (error) {
            console.error('Failed to load briefs', error);
        } finally {
            setLoading(false);
        }
    }, [userInfo?.id, isOwnProfile]);

    const refreshBriefs = React.useCallback((force = false) => {
        if (!userInfo?.id) return;
        const now = Date.now();
        if (!force && now - lastReloadAtRef.current < 1500) {
            return;
        }
        lastReloadAtRef.current = now;
        loadBriefs();
    }, [loadBriefs, userInfo?.id]);

    React.useEffect(() => {
        if (!userInfo?.id) return;
        refreshBriefs(true);

        const handleBriefsUpdated = () => {
            refreshBriefs(true);
        };

        const handleWindowFocus = () => {
            refreshBriefs();
        };

        const handleVisibilityChange = () => {
            if (document.visibilityState === 'visible') {
                refreshBriefs();
            }
        };

        window.addEventListener('briefs_updated', handleBriefsUpdated);
        window.addEventListener('focus', handleWindowFocus);
        document.addEventListener('visibilitychange', handleVisibilityChange);
        return () => {
            window.removeEventListener('briefs_updated', handleBriefsUpdated);
            window.removeEventListener('focus', handleWindowFocus);
            document.removeEventListener('visibilitychange', handleVisibilityChange);
        };
    }, [refreshBriefs, userInfo?.id]);

    const getDisplayValue = React.useCallback((value: string | undefined, defaultValue: string = '可谈') => {
        if (!value || value.trim() === '') return defaultValue;
        return value;
    }, []);

    const handleBriefClick = React.useCallback((brief: Brief) => {
        // Map Brief to BriefDetailType
        const briefDetail: BriefDetailType = {
            id: brief.id,
            title: brief.title,
            description: brief.description || "暂无详细描述",
            deliverables: brief.deliverables || "未指定交付内容",
            budgetRange: getDisplayValue(brief.budgetRange),
            deliveryTimeRange: getDisplayValue(brief.deliveryTimeRange),
            status: brief.status,
            displayOrder: brief.displayOrder,
            briefType: brief.briefType,
            contactInfo: brief.contactInfo,
            tags: brief.tags || [],
            targetDate: brief.targetDate,
            exampleWorks: brief.exampleWorks || [],
            communicationCount: brief.communicationCount,
            trialCount: brief.trialCount || 0,
            viewCount: brief.viewCount || 0
        };
        setSelectedBrief(briefDetail);
    }, [getDisplayValue]);

    React.useEffect(() => {
        if (!initialOpenBriefId || autoOpenedBriefIdRef.current === initialOpenBriefId) {
            return;
        }

        if (loading) {
            return;
        }

        const matched = briefsList.find((brief) => String(brief.id) === String(initialOpenBriefId));
        autoOpenedBriefIdRef.current = initialOpenBriefId;

        if (matched) {
            handleBriefClick(matched);
            return;
        }

        void (async () => {
            try {
                const { briefService } = await import('../../../services/briefService');
                const res = await briefService.getBriefById(initialOpenBriefId);
                const payload = res.data as any;
                const detail = payload?.data ?? payload;
                if (res.success && detail?.brief) {
                    setSelectedBrief(detail.brief);
                }
            } catch (error) {
                console.error('Failed to open shared brief:', error);
            }
        })();
    }, [briefsList, handleBriefClick, initialOpenBriefId, loading]);

    const persistBriefOrder = React.useCallback(async (nextBriefs: Brief[]) => {
        try {
            const { briefService } = await import('../../../services/briefService');
            const res = await briefService.reorderBriefs(nextBriefs.map((brief) => brief.id));
            if (!res.success) {
                throw new Error(res.error || '排序保存失败');
            }
        } catch (error) {
            console.error('Failed to reorder briefs:', error);
            showToast('需求排序保存失败，已重新加载', 'error');
            loadBriefs();
        }
    }, [loadBriefs, showToast]);

    const handleBriefDrop = React.useCallback((targetBriefId: string | number) => {
        if (!isOwnProfile || !draggingBriefId) return;

        const orderedBriefs = sortBriefsForProfile(briefsList);
        const fromIndex = orderedBriefs.findIndex((brief) => String(brief.id) === draggingBriefId);
        const toIndex = orderedBriefs.findIndex((brief) => String(brief.id) === String(targetBriefId));

        if (fromIndex < 0 || toIndex < 0 || fromIndex === toIndex) {
            return;
        }

        const nextBriefs = moveItem(orderedBriefs, fromIndex, toIndex)
            .map((brief, index) => ({ ...brief, displayOrder: index }));
        const normalized = sortBriefsForProfile(nextBriefs);
        setBriefsList(normalized);
        void persistBriefOrder(normalized);
    }, [briefsList, draggingBriefId, isOwnProfile, persistBriefOrder]);

    const content = loading
        ? (
            <div className="py-10 text-center text-gray-500 flex items-center justify-center gap-2">
                <div className="w-5 h-5 border-2 border-black border-t-transparent rounded-full animate-spin"></div>
                加载中...
            </div>
        )
        : briefsList.length === 0
            ? (
                <div className="py-20 text-center text-gray-500">暂无需求数据</div>
            )
            : (
                <div className="flex flex-col gap-4">
                    {sortBriefsForProfile(briefsList).map((brief) => (
                        <div
                            key={brief.id}
                            draggable={isOwnProfile}
                            onDragStart={(event) => {
                                if (!isOwnProfile) return;
                                setDraggingBriefId(String(brief.id));
                                event.dataTransfer.effectAllowed = 'move';
                            }}
                            onDragOver={(event) => {
                                if (isOwnProfile) event.preventDefault();
                            }}
                            onDrop={(event) => {
                                event.preventDefault();
                                handleBriefDrop(brief.id);
                            }}
                            onDragEnd={() => setDraggingBriefId(null)}
                            className={isOwnProfile ? 'cursor-grab active:cursor-grabbing' : undefined}
                        >
                            <BriefCard
                                brief={{
                                    id: brief.id,
                                    title: brief.title,
                                    budgetText: getDisplayValue(brief.budgetRange),
                                    deadlineText: getDisplayValue(brief.targetDate),
                                    durationText: getDisplayValue(brief.deliveryTimeRange),
                                    viewCount: brief.viewCount || 0,
                                    communicationCount: brief.communicationCount,
                                    trialCount: brief.trialCount || 0,
                                    status: brief.status,
                                    briefType: brief.briefType
                                } satisfies BriefCardData}
                                onClick={() => handleBriefClick(brief)}
                                showPublisher={false}
                                variant="muted"
                                dragHandle={isOwnProfile ? (
                                    <span
                                        className="mt-1 shrink-0 text-gray-300 transition-colors group-hover:text-gray-500"
                                        title="拖动排序"
                                        aria-label="拖动排序"
                                        onClick={(event) => event.stopPropagation()}
                                    >
                                        <GripVertical className="h-4 w-4" />
                                    </span>
                                ) : undefined}
                            />
                        </div>
                    ))}
                </div>
            );

    return (
        <>
            {content}

            {selectedBrief && (
                <BriefDetailModal
                    isOpen={!!selectedBrief}
                    onClose={() => setSelectedBrief(null)}
                    brief={selectedBrief}
                    isOwnProfile={isOwnProfile}
                    userInfo={userInfo}
                    onSave={(updatedBrief) => {
                        console.log('Saved brief:', updatedBrief);
                        if (updatedBrief?.status !== 'draft') {
                            setSelectedBrief(null);
                        }
                    }}
                />
            )}
        </>
    );
};

export default BriefGrids;
